import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle, Check, Pencil, Plus, Settings2, Tag, Trash2, Wallet, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiPatch, apiPost, apiDelete, apiGet } from '../../../api/client';
import type {
  DiscountType, EventBudgetSummary, Event, EventExpense, EventExpenseCategory, EventPayment, TaxMode,
} from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Input, Select } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { ProgressBar } from '../../../components/ui/ProgressBar';
import { formatCurrency, formatDate, formatPaymentMethod } from '../../../utils/format';
import { EXPENSE_CATEGORIES, EXPENSE_CATEGORY_COLORS, TAX_MODE_OPTIONS, pct, EMPTY_EXPENSE_FORM } from '../../../utils/expense';
import { ExpenseRow } from './shared/ExpenseRow';
import { MarginRow } from './shared/MarginRow';
import { ExpenseModal, getEmptyExpenseForm, type ExpenseFormState } from './shared/ExpenseModal';
import { TaxModal, type TaxFormState } from './shared/TaxModal';
import { PayRow } from './shared/PayRow';

// ─── Component ────────────────────────────────────────────────────────────────

export default function EconomicaTab({ event }: { event: Event }) {
  const qc = useQueryClient();

  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState<EventExpense | null>(null);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE_FORM);

  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({ taxMode: '', taxRate: '', taxCoefficient: '' });
  // Set to true only when the event is unconfigured (null) AND financeSettings hasn't loaded yet.
  // The useEffect will fill from globals once financeSettings arrives.
  const taxAutoFillPending = React.useRef(false);

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: budget } = useQuery<EventBudgetSummary>({
    queryKey: ['event-budget', event.id],
    queryFn: () => apiGet<EventBudgetSummary>(`/events/${event.id}/budget-summary`),
  });

  const { data: financeSettings } = useQuery<{ defaultTaxMode?: TaxMode; defaultTaxRate?: number; defaultTaxCoefficient?: number }>({
    queryKey: ['user-settings-finance'],
    queryFn: () => apiGet('/user-settings/finance'),
    staleTime: 5 * 60_000,
  });

  // ── Invalidation ────────────────────────────────────────────────────────

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['event', event.id] });
    qc.invalidateQueries({ queryKey: ['event-budget', event.id] });
  };

  // ── Mutations ────────────────────────────────────────────────────────────

  const saveExpenseMutation = useMutation({
    mutationFn: (data: object) => editingExpense
      ? apiPatch(`/events/${event.id}/expenses/${editingExpense.id}`, data)
      : apiPost(`/events/${event.id}/expenses`, data),
    onSuccess: () => {
      invalidate();
      setShowExpenseModal(false);
      toast.success(editingExpense ? 'Spesa aggiornata' : 'Spesa aggiunta');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/events/${event.id}/expenses/${id}`),
    onSuccess: () => { invalidate(); toast.success('Spesa rimossa'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePaidMutation = useMutation({
    mutationFn: ({ expenseId, isPaid }: { expenseId: string; isPaid: boolean }) =>
      apiPatch(`/events/${event.id}/expenses/${expenseId}`, { isPaid }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const patchTaxMutation = useMutation({
    mutationFn: (data: object) => apiPatch(`/events/${event.id}/tax`, data),
    onSuccess: () => {
      invalidate();
      setShowTaxModal(false);
      toast.success('Regime fiscale aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Race condition fix: if event is unconfigured (null) and financeSettings wasn't ready
  // when the modal opened, fill from globals once they arrive.
  // Does NOT fire for events explicitly set to NESSUNO (taxAutoFillPending stays false).
  useEffect(() => {
    if (!showTaxModal || !financeSettings || !taxAutoFillPending.current) return;
    taxAutoFillPending.current = false;
    const defaultMode = financeSettings.defaultTaxMode;
    if (!defaultMode || defaultMode === 'NESSUNO') return;
    setTaxForm({
      taxMode: defaultMode,
      taxRate: String(financeSettings.defaultTaxRate ?? ''),
      taxCoefficient: defaultMode === 'FORFETTARIO' ? String(financeSettings.defaultTaxCoefficient ?? '') : '',
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [financeSettings, showTaxModal]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const openAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm(EMPTY_EXPENSE_FORM);
    setShowExpenseModal(true);
  };

  const openEditExpense = (exp: EventExpense) => {
    setEditingExpense(exp);
    setExpenseForm({
      category:        exp.category,
      description:     exp.description,
      amountEstimated: String(exp.amountEstimated),
      amountActual:    exp.amountActual != null ? String(exp.amountActual) : '',
      isPaid:          exp.isPaid,
      isOwnerFee:      exp.isOwnerFee,
      ownerPresent:    exp.ownerPresent,
      notes:           exp.notes ?? '',
    });
    setShowExpenseModal(true);
  };

  const submitExpense = () => {
    if (!expenseForm.description.trim()) { toast.error('Inserisci una descrizione'); return; }
    const est = Number(expenseForm.amountEstimated);
    if (!expenseForm.amountEstimated || isNaN(est) || est < 0) { toast.error('Importo stimato non valido'); return; }
    saveExpenseMutation.mutate({
      category:        expenseForm.category,
      description:     expenseForm.description.trim(),
      amountEstimated: est,
      amountActual:    expenseForm.amountActual ? Number(expenseForm.amountActual) : null,
      isPaid:          expenseForm.isPaid,
      isOwnerFee:      expenseForm.isOwnerFee,
      ownerPresent:    expenseForm.ownerPresent,
      notes:           expenseForm.notes.trim() || null,
    });
  };

  const openTaxModal = () => {
    const storedMode = budget?.taxMode ?? event.taxMode ?? null;
    // NESSUNO = explicitly "no tax" — show blank, no fallback to globals.
    // null    = never configured — fall back to global defaults if available.
    const isExplicitNone = storedMode === 'NESSUNO';
    const isUnconfigured = storedMode === null;

    const resolvedMode = isExplicitNone
      ? ''
      : isUnconfigured
        ? (financeSettings?.defaultTaxMode === 'NESSUNO' ? '' : (financeSettings?.defaultTaxMode ?? ''))
        : storedMode;

    const resolvedRate = isExplicitNone
      ? null
      : budget?.taxRate ?? event.taxRate
        ?? (isUnconfigured ? financeSettings?.defaultTaxRate : null) ?? null;
    const resolvedCoeff = isExplicitNone
      ? null
      : budget?.taxCoefficient ?? event.taxCoefficient
        ?? (isUnconfigured ? financeSettings?.defaultTaxCoefficient : null) ?? null;

    setTaxForm({
      taxMode:        resolvedMode ?? '',
      taxRate:        resolvedRate != null ? String(resolvedRate) : '',
      taxCoefficient: resolvedCoeff != null ? String(resolvedCoeff) : '',
    });

    // Flag for async fill only when unconfigured AND globals not loaded yet
    taxAutoFillPending.current = isUnconfigured && !financeSettings;
    setShowTaxModal(true);
  };

  const submitTax = () => {
    const mode = taxForm.taxMode || 'NESSUNO';

    // Fall back to global defaults when the user left the fields blank
    const rate = taxForm.taxRate
      ? Number(taxForm.taxRate)
      : (mode !== 'NESSUNO' ? (financeSettings?.defaultTaxRate ?? null) : null);
    const coeff = taxForm.taxCoefficient
      ? Number(taxForm.taxCoefficient)
      : (mode === 'FORFETTARIO' ? (financeSettings?.defaultTaxCoefficient ?? null) : null);

    if (mode !== 'NESSUNO' && rate == null) {
      toast.error('Inserisci l\'aliquota per il regime selezionato');
      return;
    }
    if (mode === 'FORFETTARIO' && coeff == null) {
      toast.error('Inserisci il coefficiente di redditività per il regime forfettario');
      return;
    }
    patchTaxMutation.mutate({ taxMode: mode, taxRate: rate, taxCoefficient: coeff });
  };

  // ── Derived ──────────────────────────────────────────────────────────────

  const services = event.services ?? [];
  const servicesWithCost = services.filter(s => s.unitCost != null);
  const hasCostTracking = servicesWithCost.length > 0;
  const totalServiceCost = servicesWithCost.reduce((sum, s) => sum + (s.unitCost ?? 0), 0);
  const totalServiceRevenue = services.reduce((sum, s) => {
    const p = Number(s.unitPrice);
    if (s.discountType === 'FIXED' && s.discount != null) return sum + Math.max(0, p - Number(s.discount));
    if (s.discountType === 'PERCENTAGE' && s.discount != null) return sum + p * (1 - Number(s.discount) / 100);
    return sum + p;
  }, 0);
  const serviceMargin = totalServiceRevenue - totalServiceCost;
  const serviceMarginPct = totalServiceRevenue > 0
    ? Math.round(((serviceMargin) / totalServiceRevenue) * 100) : 0;

  const expenses         = event.expenses ?? [];
  const expensesActive   = expenses.filter(e => !(e.isOwnerFee && !e.ownerPresent));
  const expensesExcluded = expenses.filter(e => e.isOwnerFee && !e.ownerPresent);
  const collaborators    = (event.teams ?? []).flatMap(t => t.slots);

  // Local calculations — fallback when budget endpoint is unavailable or stale
  const collaboratorsFeeLocal = collaborators.reduce(
    (sum, ec) => sum + Number(ec.agreedFee ?? ec.proposedFee ?? 0), 0,
  );
  const expensesLocalTotal = expensesActive.reduce(
    (sum, e) => sum + Number(e.amountActual ?? e.amountEstimated), 0,
  );
  const totalExpensesLocal = totalServiceCost + collaboratorsFeeLocal + expensesLocalTotal;
  const servicesNetLocal = totalServiceRevenue; // already net of discounts
  const totalAgreed = Number(event.totalAmount);
  const revenueBaseLocal = totalAgreed > 0 ? totalAgreed : (budget?.servicesNet ?? servicesNetLocal);
  const grossMarginLocal = revenueBaseLocal - totalExpensesLocal;
  const overBudgetLocal = totalExpensesLocal > revenueBaseLocal;

  const { data: payments = [] } = useQuery({
    queryKey: ['event-payments', event.id],
    queryFn: () => apiGet(`/events/${event.id}/payments`),
    enabled: !!event.id,
  });

  const paymentsList = (payments as any[]).length > 0
    ? (payments as any[])
    : (event.depositPaidAt
      ? [{ id: 'legacy-deposit', amount: Number(event.depositAmount ?? 0), paidAt: event.depositPaidAt!, method: event.depositPaidMethod ?? 'CONTANTI', notes: '' }]
      : []).concat(event.finalPaidAt
        ? [{ id: 'legacy-final', amount: Number(event.finalAmount ?? 0), paidAt: event.finalPaidAt!, method: event.finalPaidMethod ?? 'CONTANTI', notes: '' }]
        : []);

  const totalPaid = paymentsList.reduce((s: number, p: any) => s + Number(p.amount), 0);
  const remaining = totalAgreed - totalPaid;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Over-budget alert */}
      {budget?.overBudget && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
          background: 'var(--color-error-highlight)',
          border: '1px solid var(--color-error)',
          borderRadius: 'var(--radius-card)', padding: '0.75rem 1rem',
        }}>
          <AlertTriangle size={15} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: '0.1rem' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.5 }}>
            <strong>Attenzione — Budget superato:</strong> i costi stimati ({formatCurrency(budget.totalExpenses)}) superano
            i ricavi netti di{' '}
            <strong style={{ color: 'var(--color-error)' }}>{formatCurrency(Math.abs(budget.grossMargin))}</strong>.
          </p>
        </div>
      )}

      {/* ── Incassi ──────────────────────────────────────────────────────── */}
      <div style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1.25rem',
      }}>
        <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, marginBottom: '1rem' }}>Incassi</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <PayRow
            label="Totale concordato"
            value={formatCurrency(totalAgreed)}
            bold
          />
          {paymentsList.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '0.25rem' }}>Pagamenti ricevuti</span>
              {paymentsList.map((p: any) => (
                <PayRow
                  key={p.id}
                  label={formatDate(p.paidAt)}
                  value={formatCurrency(Number(p.amount))}
                  sub={formatPaymentMethod(p.method) + (p.notes ? ` · ${p.notes}` : '')}
                  status="paid"
                />
              ))}
            </div>
          )}
          {remaining > 0 && (
            <PayRow label="Residuo da incassare" value={formatCurrency(remaining)} valueColor="var(--color-warning)" bold />
          )}
        </div>
        {totalPaid > 0 && totalAgreed > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <ProgressBar value={pct(totalPaid, totalAgreed)} color="var(--color-success)" height={4} />
            <span style={{ fontSize: '10px', color: 'var(--color-text-faint)', display: 'block', textAlign: 'right', marginTop: '0.25rem' }}>
              {((totalPaid / totalAgreed) * 100).toFixed(0)}% incassato
            </span>
          </div>
        )}
      </div>

      {/* ── Ricavi (servizi) ─────────────────────────────────────────────── */}
      {(event.services ?? []).length > 0 && budget && (
        <div style={{
          background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
          borderRadius: 'var(--radius-card)', padding: '1.25rem',
        }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, marginBottom: '1rem' }}>Ricavi da servizi</h3>
          <div>
            {(event.services ?? []).map(es => {
              const effectivePrc = (() => {
                const price = Number(es.unitPrice);
                if (es.discountType === 'FIXED' && es.discount != null)
                  return Math.max(0, price - Number(es.discount));
                if (es.discountType === 'PERCENTAGE' && es.discount != null)
                  return price * (1 - Number(es.discount) / 100);
                return price;
              })();
              const hasDiscount = es.discountType != null && es.discount != null;
              return (
                <div key={es.id} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)', gap: '1rem',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 'var(--text-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {es.label}
                    </span>
                    {hasDiscount && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600, color: 'var(--color-primary)', flexShrink: 0,
                        background: 'oklch(from var(--color-primary) l c h / 0.12)',
                        padding: '0.0625rem 0.375rem', borderRadius: 'var(--radius-interactive)',
                      }}>
                        {es.discountType === 'PERCENTAGE' ? `−${es.discount}%` : `−${formatCurrency(es.discount ?? 0)}`}
                      </span>
                    )}
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    {hasDiscount && (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through', display: 'block' }}>
                        {formatCurrency(es.unitPrice)}
                      </span>
                    )}
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-gold)' }}>
                      {formatCurrency(effectivePrc)}
                    </span>
                  </div>
                </div>
              );
            })}
            <div style={{ paddingTop: '0.625rem', display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              {budget.totalDiscount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)' }}>
                    <Tag size={10} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />
                    Sconti totali
                  </span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 600 }}>
                    −{formatCurrency(budget.totalDiscount)}
                  </span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: budget.totalDiscount > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingTop: budget.totalDiscount > 0 ? '0.25rem' : 0 }}>
                <span style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>
                  {budget.totalDiscount > 0 ? 'Netto: ' : 'Totale: '}{formatCurrency(budget.servicesNet)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Costi (produzione + collaboratori + spese evento) ──────────────── */}
      <div style={{
        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 'var(--radius-card)', padding: '1.25rem',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: (hasCostTracking || collaborators.length > 0 || expenses.length > 0) ? '1rem' : 0,
        }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Costi</h3>
          <Button size="sm" variant="secondary" onClick={openAddExpense} icon={<Plus size={14} />}>
            Aggiungi
          </Button>
        </div>

        {/* Stato vuoto */}
        {!hasCostTracking && collaborators.length === 0 && expenses.length === 0 && (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '1rem' }}>
            Nessun costo registrato
          </p>
        )}

        {/* Produzione (read-only, da catalogo) */}
        {hasCostTracking && (
          <div>
            <span style={{
              fontSize: '10px', fontWeight: 600, color: 'var(--color-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'block', marginBottom: '0.375rem',
            }}>
              Produzione
            </span>
            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginBottom: '0.5rem' }}>
              Costi diretti dei servizi tracciati dal catalogo prodotti.
            </p>
            {servicesWithCost.map(s => {
              const netP = (() => {
                const p = Number(s.unitPrice);
                if (s.discountType === 'FIXED' && s.discount != null) return Math.max(0, p - Number(s.discount));
                if (s.discountType === 'PERCENTAGE' && s.discount != null) return p * (1 - Number(s.discount) / 100);
                return p;
              })();
              const cost = s.unitCost ?? 0;
              const mPct = netP > 0 ? Math.round(((netP - cost) / netP) * 100) : 0;
              const mColor = mPct >= 50 ? 'var(--color-success)' : mPct >= 25 ? 'var(--color-warning)' : 'var(--color-error)';
              return (
                <div key={s.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.5rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)', gap: '1rem',
                }}>
                  <span style={{ fontSize: 'var(--text-sm)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0 }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      prezzo: <strong>{formatCurrency(netP)}</strong>
                    </span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      costo: <strong>{formatCurrency(cost)}</strong>
                    </span>
                    <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, color: mColor,
                      background: `oklch(from ${mColor} l c h / 0.1)`,
                      padding: '0.1rem 0.375rem', borderRadius: 'var(--radius-interactive)' }}>
                      {mPct}%
                    </span>
                  </div>
                </div>
              );
            })}
            <div style={{ paddingTop: '0.625rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Totale costi di produzione</span>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{formatCurrency(totalServiceCost)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>Margine sui prodotti</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{
                    fontSize: 'var(--text-xs)', fontWeight: 700,
                    color: serviceMarginPct >= 50 ? 'var(--color-success)' : serviceMarginPct >= 25 ? 'var(--color-warning)' : 'var(--color-error)',
                    background: 'oklch(from var(--color-success) l c h / 0.1)',
                    padding: '0.1rem 0.375rem', borderRadius: 'var(--radius-interactive)',
                  }}>
                    {serviceMarginPct}%
                  </span>
                  <span style={{
                    fontSize: 'var(--text-sm)', fontWeight: 700,
                    color: serviceMargin >= 0 ? 'var(--color-success)' : 'var(--color-error)',
                  }}>
                    {formatCurrency(serviceMargin)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Collaboratori (read-only) */}
        {collaborators.length > 0 && (
          <div style={{ marginTop: hasCostTracking ? '1rem' : 0, borderTop: hasCostTracking ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingTop: hasCostTracking ? '1rem' : 0 }}>
            <span style={{
              fontSize: '10px', fontWeight: 600, color: 'var(--color-text-muted)',
              textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'block', marginBottom: '0.375rem',
            }}>
              Collaboratori
            </span>
            {collaborators.map(ec => {
              const effectiveFee = ec.agreedFee != null ? ec.agreedFee : ec.proposedFee;
              const hasOverride = ec.agreedFee != null;
              return (
                <div key={ec.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)',
                }}>
                  <div>
                    <span style={{ fontSize: 'var(--text-sm)' }}>{ec.collaborator?.name ?? ec.role}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginLeft: '0.5rem' }}>{ec.role}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    {effectiveFee != null ? (
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {formatCurrency(effectiveFee)}
                      </span>
                    ) : (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>—</span>
                    )}
                    {hasOverride && (
                      <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '9999px', border: '1px solid #3E91FF', background: 'rgba(62,145,255,0.08)', color: '#60A5FA' }}>
                        override
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.375rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Totale compensi</span>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-primary)' }}>
                {formatCurrency(budget?.collaboratorsFee ?? collaboratorsFeeLocal)}
              </span>
            </div>
          </div>
        )}

        {/* Spese evento (CRUD) */}
        {(expenses.length > 0 || (!hasCostTracking && collaborators.length === 0)) && (
          <div style={{
            marginTop: (hasCostTracking || collaborators.length > 0) ? '1rem' : 0,
            borderTop: (hasCostTracking || collaborators.length > 0) ? '1px solid rgba(255,255,255,0.08)' : 'none',
            paddingTop: (hasCostTracking || collaborators.length > 0) ? '1rem' : 0,
          }}>
            {(hasCostTracking || collaborators.length > 0) && (
              <span style={{
                fontSize: '10px', fontWeight: 600, color: 'var(--color-text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
                display: 'block', marginBottom: '0.375rem',
              }}>
                Spese evento
              </span>
            )}
            {expensesActive.map(exp => (
              <ExpenseRow
                key={exp.id} expense={exp}
                onEdit={() => openEditExpense(exp)}
                onDelete={() => deleteExpenseMutation.mutate(exp.id)}
                onTogglePaid={() => togglePaidMutation.mutate({ expenseId: exp.id, isPaid: !exp.isPaid })}
              />
            ))}
            {expensesExcluded.length > 0 && (
              <>
                <div style={{ padding: '0.625rem 0 0.375rem', marginTop: expensesActive.length > 0 ? '0.25rem' : 0 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                    Titolare non presente — non contano come costo
                  </span>
                </div>
                {expensesExcluded.map(exp => (
                  <ExpenseRow
                    key={exp.id} expense={exp} excluded
                    onEdit={() => openEditExpense(exp)}
                    onDelete={() => deleteExpenseMutation.mutate(exp.id)}
                    onTogglePaid={() => togglePaidMutation.mutate({ expenseId: exp.id, isPaid: !exp.isPaid })}
                  />
                ))}
              </>
            )}
          </div>
        )}

        {/* Totale costi */}
        {(hasCostTracking || collaborators.length > 0 || expenses.length > 0) && (
          <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {(budget?.ownerFeeExcluded ?? 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>
                  Escluse (titolare assente)
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>
                  {formatCurrency(budget?.ownerFeeExcluded ?? 0)}
                </span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Totale costi</span>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>{formatCurrency(budget?.totalExpenses ?? totalExpensesLocal)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Margini ───────────────────────────────────────────────────────── */}
      {(event.services ?? []).length > 0 && (
        <div style={{
          background: 'var(--glass-bg-card)',
          border: `1px solid ${(budget?.overBudget ?? overBudgetLocal) ? 'var(--color-error)' : 'rgba(255,255,255,0.10)'}`,
          borderRadius: 'var(--radius-card)', padding: '1.25rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Margini</h3>
            <button
              onClick={openTaxModal}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.375rem',
                background: 'none', border: '1px solid rgba(255,255,255,0.10)',
                borderRadius: 'var(--radius-interactive)', padding: '0.25rem 0.625rem',
                cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)',
              }}
            >
              <Settings2 size={12} /> Imposte
            </button>
          </div>

          <MarginRow
            label={(budget?.totalDiscount ?? 0) > 0 ? 'Ricavi netti (dopo sconti)' : 'Ricavi'}
            value={revenueBaseLocal}
            valueColor="var(--color-gold)"
          />
          {hasCostTracking && (
            <MarginRow label="− Costi di produzione" value={-(budget?.productionCosts ?? totalServiceCost)} />
          )}
          {(budget?.supplierCosts ?? 0) > 0 && (
            <MarginRow label="− Costi fornitori" value={-budget!.supplierCosts} />
          )}
          {(budget?.collaboratorsFee ?? collaboratorsFeeLocal) > 0 && (
            <MarginRow label="− Compensi collaboratori" value={-(budget?.collaboratorsFee ?? collaboratorsFeeLocal)} />
          )}
          {(budget?.otherExpenses ?? expensesLocalTotal) > 0 && (
            <MarginRow label="− Spese evento" value={-(budget?.otherExpenses ?? expensesLocalTotal)} />
          )}
          {(budget?.ownerFeeExcluded ?? 0) > 0 && (
            <MarginRow
              label="Escluse (titolare assente)"
              value={0}
              sub={`${formatCurrency(budget?.ownerFeeExcluded ?? 0)} non conteggiati nel margine`}
              faint
            />
          )}

          <div style={{ marginTop: '0.625rem', paddingTop: '0.625rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <MarginRow
              label="Margine prima delle tasse"
              value={budget?.grossMargin ?? grossMarginLocal}
              bold
              valueColor={(budget?.grossMargin ?? grossMarginLocal) >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
            />
            <div style={{ marginTop: '0.375rem' }}>
              <ProgressBar
                value={pct(Math.max(0, budget?.grossMargin ?? grossMarginLocal), revenueBaseLocal)}
                color={(budget?.grossMargin ?? grossMarginLocal) >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
                height={4}
              />
            </div>
          </div>

          {budget?.taxMode && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{
                  fontSize: '10px', fontWeight: 700, color: '#FFFFFF',
                  background: 'var(--color-primary)',
                  padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-interactive)',
                }}>
                  {budget!.taxMode === 'FORFETTARIO' ? 'Forfettario' : 'Ordinario'}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                  {budget!.taxMode === 'FORFETTARIO' && budget!.taxCoefficient != null
                    ? `coeff. ${budget!.taxCoefficient}% · aliq. ${budget!.taxRate}%`
                    : `aliq. ${budget!.taxRate}%`}
                </span>
              </div>
              {budget!.taxMode === 'FORFETTARIO' && (
                <MarginRow
                  label="Base imponibile"
                  value={budget!.taxableBase}
                  sub={`${budget!.taxCoefficient}% del margine lordo`}
                />
              )}
              <MarginRow label="− Imposte stimate" value={-budget!.taxAmount} valueColor="var(--color-text-muted)" />
            </div>
          )}

          {/* Il tuo guadagno — prominent box */}
          {(() => {
            const finalValue = budget?.taxMode ? (budget.netProfit ?? 0) : (budget?.grossMargin ?? grossMarginLocal);
            const isPositive = finalValue >= 0;
            const profitPct  = revenueBaseLocal > 0 ? ((finalValue / revenueBaseLocal) * 100) : 0;
            const boxColor   = isPositive ? 'var(--color-success)' : 'var(--color-error)';
            const noTax      = !budget?.taxMode;
            return (
              <div style={{
                marginTop: '1.25rem',
                border: `2px solid ${noTax ? 'var(--color-warning)' : boxColor}`,
                borderRadius: 'var(--radius-card)', padding: '1rem 1.25rem',
                background: noTax
                  ? 'oklch(from var(--color-warning) l c h / 0.06)'
                  : `oklch(from ${boxColor} l c h / 0.06)`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Wallet size={16} style={{ color: noTax ? 'var(--color-warning)' : boxColor, flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                      Il tuo guadagno
                    </span>
                    {noTax && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#FFFFFF', background: 'var(--color-warning)', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', whiteSpace: 'nowrap' }}>
                        Senza calcolo tasse
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 'var(--text-xl)', fontWeight: 800, color: boxColor, fontVariantNumeric: 'tabular-nums' }}>
                    {isPositive ? '' : '−'}{formatCurrency(Math.abs(finalValue))}
                  </span>
                </div>

                <div style={{ marginTop: '0.75rem' }}>
                  <ProgressBar
                    value={pct(Math.max(0, finalValue), revenueBaseLocal)}
                    color={boxColor}
                    height={8}
                  />
                  {revenueBaseLocal > 0 && (
                    <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', textAlign: 'right', marginTop: '0.25rem' }}>
                      {profitPct.toFixed(1)}% del ricavo netto
                    </span>
                  )}
                </div>

                {noTax && (
                  <button
                    onClick={openTaxModal}
                    style={{ marginTop: '0.75rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-primary)', textDecoration: 'underline', display: 'block' }}
                  >
                    Configura regime fiscale →
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      {/* Expense modal */}
      <Modal
        open={showExpenseModal}
        onClose={() => setShowExpenseModal(false)}
        title={editingExpense ? 'Modifica spesa' : 'Aggiungi spesa'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowExpenseModal(false)}>Annulla</Button>
            <Button onClick={submitExpense} loading={saveExpenseMutation.isPending}>
              {editingExpense ? 'Salva' : 'Aggiungi'}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select
            label="Categoria *"
            value={expenseForm.category}
            onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value as EventExpenseCategory }))}
            options={EXPENSE_CATEGORIES}
          />
          <Input
            label="Descrizione *"
            value={expenseForm.description}
            onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))}
            placeholder="es. Carburante A/R, Album GraphiStudio..."
          />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input
              label="Importo stimato (€) *"
              type="number" min="0" step="0.01"
              value={expenseForm.amountEstimated}
              onChange={e => setExpenseForm(f => ({ ...f, amountEstimated: e.target.value }))}
            />
            <Input
              label="Importo effettivo (€)"
              type="number" min="0" step="0.01"
              value={expenseForm.amountActual}
              onChange={e => setExpenseForm(f => ({ ...f, amountActual: e.target.value }))}
              placeholder="Se diverso da stimato"
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox" checked={expenseForm.isOwnerFee}
              onChange={e => setExpenseForm(f => ({ ...f, isOwnerFee: e.target.checked, ownerPresent: e.target.checked ? f.ownerPresent : true }))}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
            />
            <span style={{ fontSize: 'var(--text-sm)' }}>Compenso titolare</span>
          </label>
          {expenseForm.isOwnerFee && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', cursor: 'pointer', userSelect: 'none', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.625rem 0.75rem', border: '1px solid rgba(255,255,255,0.10)' }}>
              <input
                type="checkbox" checked={expenseForm.ownerPresent}
                onChange={e => setExpenseForm(f => ({ ...f, ownerPresent: e.target.checked }))}
                style={{ width: 16, height: 16, cursor: 'pointer', marginTop: '0.125rem', accentColor: 'var(--color-primary)', flexShrink: 0 }}
              />
              <div>
                <span style={{ fontSize: 'var(--text-sm)', display: 'block' }}>Titolare presente all'evento</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                  {expenseForm.ownerPresent
                    ? 'La spesa viene conteggiata nei costi'
                    : 'Non spuntato → esclusa dal margine (titolare non presente = ricavo pieno)'}
                </span>
              </div>
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox" checked={expenseForm.isPaid}
              onChange={e => setExpenseForm(f => ({ ...f, isPaid: e.target.checked }))}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-primary)' }}
            />
            <span style={{ fontSize: 'var(--text-sm)' }}>Già pagata</span>
          </label>
          <Input
            label="Note (opzionale)"
            value={expenseForm.notes}
            onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="Note aggiuntive..."
          />
        </div>
      </Modal>

      {/* Tax modal */}
      <Modal
        open={showTaxModal}
        onClose={() => setShowTaxModal(false)}
        title="Configura imposte"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowTaxModal(false)}>Annulla</Button>
            <Button onClick={submitTax} loading={patchTaxMutation.isPending}>Salva</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select
            label="Regime fiscale"
            value={taxForm.taxMode}
            onChange={e => {
              const newMode = e.target.value;
              setTaxForm(f => ({
                ...f,
                taxMode: newMode,
                // Auto-fill rate from global defaults if field is empty and user picks an active mode
                taxRate: (newMode && newMode !== '' && !f.taxRate)
                  ? String(financeSettings?.defaultTaxRate ?? '')
                  : f.taxRate,
                // Auto-fill coeff from global defaults if switching to forfettario and field is empty
                taxCoefficient: newMode !== 'FORFETTARIO'
                  ? ''
                  : (f.taxCoefficient || String(financeSettings?.defaultTaxCoefficient ?? '')),
              }));
            }}
            options={TAX_MODE_OPTIONS}
          />
          {taxForm.taxMode === 'FORFETTARIO' && (
            <Input
              label="Coefficiente di redditività (%)"
              type="number" min="0" max="100" step="1"
              value={taxForm.taxCoefficient}
              onChange={e => setTaxForm(f => ({ ...f, taxCoefficient: e.target.value }))}
              placeholder="es. 78"
            />
          )}
          {taxForm.taxMode !== '' && (
            <Input
              label={taxForm.taxMode === 'FORFETTARIO' ? 'Aliquota imposta (%)' : 'Aliquota IRPEF (%)'}
              type="number" min="0" max="100" step="0.5"
              value={taxForm.taxRate}
              onChange={e => setTaxForm(f => ({ ...f, taxRate: e.target.value }))}
              placeholder={taxForm.taxMode === 'FORFETTARIO' ? 'es. 15' : 'es. 26'}
            />
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic', lineHeight: 1.5 }}>
            I valori di default vengono da <strong>Impostazioni → Finanza</strong>.
            Modificarli qui sovrascrive solo questo evento.
          </p>
        </div>
      </Modal>
    </div>
  );
}
